<template>
  <div class="my-reads-container">
    <div class="header-row">
      <h2>Riwayat Baca Saya</h2>
      <button @click="refreshReadingHistory" class="refresh-button" :disabled="loading">
        <font-awesome-icon :icon="['fas', 'sync']" :class="{ 'fa-spin': loading }" />
        {{ loading ? 'Memuat...' : 'Refresh' }}
      </button>
    </div>
    
    <!-- Reading Stats Summary Card -->
    <div class="reading-stats-card" v-if="readingStats.totalBooks > 0">
      <div class="stats-header">
        <font-awesome-icon :icon="['fas', 'chart-line']" class="stats-icon" />
        <h3>Statistik Bacaan</h3>
      </div>
      <div class="stats-body">
        <div class="stat-item">
          <div class="stat-value">{{ readingStats.totalBooks }}</div>
          <div class="stat-label">Buku Dibaca</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ readingStats.totalChapters }}</div>
          <div class="stat-label">Bab Dibaca</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ readingStats.formattedTotalTime }}</div>
          <div class="stat-label">Total Waktu Baca</div>
        </div>
        <div class="stat-item">
          <div class="stat-value">{{ readingStats.deviceDistribution.Mobile }}%</div>
          <div class="stat-label">Dari Mobile</div>
        </div>
      </div>
    </div>
    
    <div v-if="loading && chapterViews.length === 0" class="loading">
      <p>Loading...</p>
    </div>
    <div v-else-if="chapterViews.length > 0">
      <table class="reads-table">
        <thead>
          <tr>
            <th>Buku</th>
            <th>Bab</th>
            <th>Durasi Baca</th>
            <th class="hide-on-mobile">Perangkat</th>
            <th>Terakhir Dibaca</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="view in chapterViews" :key="`${view.book_slug || view.chapter?.book?.slug}-${view.chapter_slug || view.chapter?.slug}`">
            <td>
              <router-link :to="`/book/${view.book_slug || view.chapter?.book?.slug}`">{{ view.book_title || view.chapter?.book?.title }}</router-link>
            </td>
            <td>
              <router-link :to="`/books/${view.book_slug || view.chapter?.book?.slug}/chapters/${view.chapter_slug || view.chapter?.slug}`">{{ view.chapter_title || view.chapter?.title }}</router-link>
            </td>
            <td>
              <span class="duration-badge">
                <font-awesome-icon :icon="['fas', 'clock']" /> 
                {{ view.formatted_duration || formatDuration(view.read_duration) }}
              </span>
            </td>
            <td class="hide-on-mobile">
              <span :class="['device-badge', view.device_type === 'Mobile' ? 'mobile' : 'desktop']">
                <font-awesome-icon :icon="view.device_type === 'Mobile' ? ['fas', 'mobile-alt'] : ['fas', 'desktop']" />
                {{ view.device_type || "Desktop" }}
              </span>
            </td>
            <td>{{ formatDate(view.timestamp) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-if="loading" class="loading">
        <p>Loading more...</p>
      </div>
      <div v-if="!loading && !nextUrl && chapterViews.length > 0" class="end-of-list">
        <p>Anda telah mencapai akhir daftar.</p>
      </div>
    </div>
    <div v-else class="no-reads">
      <p>Anda belum membaca bab apa pun.</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'MyRead',
  data() {
    return {
      chapterViews: [],
      loading: false,
      nextUrl: null,
      readingStats: {
        totalBooks: 0,
        totalChapters: 0,
        totalReadTime: 0,
        formattedTotalTime: '0 menit',
        deviceDistribution: {
          Desktop: 0,
          Mobile: 0
        }
      }
    };
  },
  async created() {
    await this.fetchChapterViews();
    await this.calculateReadingStats();
    window.addEventListener('scroll', this.handleScroll); // Add scroll listener
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.handleScroll); // Remove listener
  },
  methods: {
    refreshReadingHistory() {
      console.log('Refreshing reading history');
      // Reset state
      this.chapterViews = [];
      this.nextUrl = null;
      // Fetch fresh data
      this.fetchChapterViews().then(() => {
        this.calculateReadingStats();
      });
    },
    
    async fetchChapterViews() {
      if (this.loading) return;

      this.loading = true;
      const token = localStorage.getItem('token');
      const url = this.nextUrl || `${process.env.VUE_APP_API_BASE_URL}/api/my-reads/`;
      
      try {
        console.log('Fetching chapter views from:', url);
        
        const response = await axios.get(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        console.log('Response received:', response.data);
        
        // Handle paginated response (DRF standard)
        if (response.data && typeof response.data === 'object' && response.data.results) {
          console.log('Processing paginated response with results array');
          this.chapterViews = this.chapterViews.concat(response.data.results);
          this.nextUrl = response.data.next;
        } 
        // Handle direct array response
        else if (Array.isArray(response.data)) {
          console.log('Processing direct array response');
          this.chapterViews = this.chapterViews.concat(response.data);
          // Check if we have pagination info in headers
          this.nextUrl = response.headers['next-page'] || null;
        }
        // Handle other object responses
        else if (response.data && typeof response.data === 'object') {
          console.log('Processing object response, converting to array if needed');
          // Some APIs might return the data in a different structure
          const dataArray = response.data.data || response.data.items || [response.data];
          this.chapterViews = this.chapterViews.concat(dataArray);
          this.nextUrl = response.data.next || response.data.nextPage || null;
        }
        
        // Process the data to ensure all needed properties exist
        this.chapterViews = this.chapterViews.map(view => {
          // Make sure we have all the required properties
          return {
            book_title: view.book_title || view.chapter?.book?.title || 'Unknown Book',
            book_slug: view.book_slug || view.chapter?.book?.slug || '',
            chapter_title: view.chapter_title || view.chapter?.title || 'Unknown Chapter',
            chapter_slug: view.chapter_slug || view.chapter?.slug || '',
            read_duration: view.read_duration || 0,
            formatted_duration: view.formatted_duration || this.formatDuration(view.read_duration || 0),
            device_type: view.device_type || 'Desktop',
            timestamp: view.timestamp || view.created_at || view.viewed_at || new Date().toISOString()
          };
        });
        
        console.log('Processed chapter views:', this.chapterViews);

      } catch (error) {
        console.error('Failed to fetch chapter views', error);
        console.error('Error details:', error.response?.data);
        
        if (error.response) {
          if (error.response.status === 401) {
            console.log('Authentication error, redirecting to login');
            this.$router.push('/login');
          } else {
            console.error('Server error:', error.response.status, error.response.data);
          }
        } else if (error.request) {
          console.error('No response received from server');
        } else {
          console.error('Error setting up request:', error.message);
        }
      } finally {
        this.loading = false;
      }
    },
    
    formatDate(dateString) {
      if (!dateString) return "Unknown";
      
      try {
        const date = new Date(dateString);
        
        // Check if date is valid
        if (isNaN(date.getTime())) return "Unknown";
        
        return date.toLocaleString("id-ID", {
          year: "numeric",
          month: "long",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
        });
      } catch (error) {
        console.error("Error formatting date:", error);
        return "Unknown";
      }
    },
    
    formatDuration(seconds) {
      if (!seconds || seconds < 1) return "< 1 detik";
      
      if (seconds < 60) {
        return `${seconds} detik`;
      } else if (seconds < 3600) {
        const minutes = Math.floor(seconds / 60);
        return `${minutes} menit`;
      } else {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        return `${hours} jam ${minutes > 0 ? minutes + ' menit' : ''}`;
      }
    },
    
    handleScroll() {
      // Check if the user has scrolled to the bottom
      const scrollPosition = window.innerHeight + window.scrollY;
      const threshold = document.body.offsetHeight - 150; // More generous threshold for better UX
      
      if (scrollPosition >= threshold && this.nextUrl && !this.loading) {
          console.log('Scroll threshold reached, loading more items');
          this.fetchChapterViews().then(() => {
            this.calculateReadingStats();
          });
      }
    },
    
    calculateReadingStats() {
      if (!this.chapterViews.length) return;
      
      // Set of unique books
      const bookSet = new Set();
      
      // Total read duration
      let totalDuration = 0;
      
      // Count for devices
      let mobileCount = 0;
      let desktopCount = 0;
      
      // Process each view
      this.chapterViews.forEach(view => {
        // Add book to set
        bookSet.add(view.book_slug || view.chapter?.book?.slug);
        
        // Add duration
        totalDuration += view.read_duration || 0;
        
        // Count device
        if (view.device_type === 'Mobile') {
          mobileCount++;
        } else {
          desktopCount++;
        }
      });
      
      // Calculate device distribution percentages
      const total = mobileCount + desktopCount;
      let mobilePercent = 0;
      let desktopPercent = 0;
      
      if (total > 0) {
        mobilePercent = Math.round((mobileCount / total) * 100);
        desktopPercent = 100 - mobilePercent;
      }
      
      // Update stats
      this.readingStats = {
        totalBooks: bookSet.size,
        totalChapters: this.chapterViews.length,
        totalReadTime: totalDuration,
        formattedTotalTime: this.formatDuration(totalDuration),
        deviceDistribution: {
          Mobile: mobilePercent,
          Desktop: desktopPercent
        }
      };
    }
  },
};
</script>

<style scoped>
.my-reads-container {
  max-width: 1000px;
  margin: 40px auto;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.1);
}

.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.refresh-button {
  background-color: #4CAF50;
  color: white;
  border: none;
  padding: 8px 12px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: background-color 0.3s;
}

.refresh-button:hover {
  background-color: #45a049;
}

.refresh-button:disabled {
  background-color: #cccccc;
  cursor: not-allowed;
}

/* Reading Stats Card */
.reading-stats-card {
  background-color: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 24px;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.05);
}

.stats-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  color: #333;
}

.stats-icon {
  font-size: 20px;
  color: #4CAF50;
}

.stats-header h3 {
  margin: 0;
  font-size: 18px;
}

.stats-body {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  justify-content: space-between;
}

.stat-item {
  flex: 1;
  min-width: 120px;
  background-color: white;
  border-radius: 6px;
  padding: 12px;
  text-align: center;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
}

.stat-value {
  font-size: 22px;
  font-weight: bold;
  color: #333;
}

.stat-label {
  font-size: 14px;
  color: #666;
  margin-top: 4px;
}

/* Table styles */
.loading {
  text-align: center;
  margin-top: 50px;
}

.reads-table {
  width: 100%;
  border-collapse: collapse;
  margin-top: 20px;
  table-layout: fixed;
}

.reads-table th,
.reads-table td {
  padding: 12px 15px;
  text-align: left;
  border-bottom: 1px solid #ddd;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.reads-table th {
  background-color: #f8f9fa;
  font-weight: bold;
  color: #495057;
}

.reads-table tr:hover {
  background-color: #f5f5f5;
}

.no-reads {
  text-align: center;
  padding: 30px;
  color: #666;
  font-style: italic;
}

.reads-table a {
  text-decoration: none;
  color: #f66a24;
  font-weight: 500;
}

.reads-table a:hover {
  text-decoration: underline;
}

.end-of-list {
  text-align: center;
  margin-top: 20px;
  color: #888;
  font-style: italic;
}

/* Duration and device badge */
.duration-badge {
  display: inline-flex;
  align-items: center;
  background-color: #e8f5e9;
  color: #2e7d32;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
  gap: 5px;
}

.device-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
}

.device-badge.desktop {
  background-color: #e3f2fd;
  color: #1565c0;
}

.device-badge.mobile {
  background-color: #fff3e0;
  color: #e65100;
}

/* Responsive design for mobile */
@media screen and (max-width: 768px) {
  .hide-on-mobile {
    display: none;
  }
  
  .reads-table {
    font-size: 14px;
  }
  
  .reads-table th, 
  .reads-table td {
    padding: 8px 6px;
  }
  
  .stats-body {
    flex-direction: column;
    gap: 10px;
  }
  
  .stat-item {
    flex: none;
    width: 100%;
  }
  
  .header-row {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
  }
  
  .refresh-button {
    align-self: flex-end;
  }
}
</style>